import { ArrowRight, BookOpenText, Eye, EyeOff, Sparkles } from "lucide-react";
import { type FormEvent, useState } from "react";

import { postJson } from "../api";
import { useAuth } from "../auth";
import { Logo } from "../components/Logo";
import type { TokenResponse } from "../types";
import { errorMessage } from "../utils";

export function AuthPage() {
  const { login } = useAuth();
  const [mode, setMode] = useState<"login" | "register">("login");
  const [showPassword, setShowPassword] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    setError("");
    const form = new FormData(event.currentTarget);
    const body = {
      username: String(form.get("username") ?? ""),
      password: String(form.get("password") ?? ""),
      ...(mode === "register"
        ? {
            display_name: String(form.get("display_name") ?? ""),
            registration_code: String(form.get("registration_code") ?? "")
          }
        : {})
    };
    try {
      login(await postJson<TokenResponse>(`/api/auth/${mode}`, body));
    } catch (reason) {
      setError(errorMessage(reason));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="auth-page">
      <section className="auth-story">
        <Logo />
        <div className="auth-story__content">
          <span className="eyebrow"><Sparkles size={15} /> Lire pour apprendre</span>
          <h1>把法语词汇，<br />放回真正的阅读里。</h1>
          <p>
            先读，先标记，不让查词打断思路。然后集中预习、理解并记录每一次真实进步。
          </p>
          <div className="auth-quote">
            <BookOpenText size={24} />
            <span>今天的目标不是背更多卡片，而是多读完一段法语。</span>
          </div>
        </div>
      </section>
      <section className="auth-panel">
        <div className="auth-panel__inner">
          <span className="eyebrow">{mode === "login" ? "Bon retour" : "Commencer"}</span>
          <h2>{mode === "login" ? "继续你的阅读" : "建立阅读档案"}</h2>
          <p className="muted">
            {mode === "login" ? "登录后同步书籍、标注和词汇状态。" : "先完成一次短测试，再从一本书开始。"}
          </p>
          <div className="segmented">
            <button className={mode === "login" ? "active" : ""} onClick={() => setMode("login")}>登录</button>
            <button className={mode === "register" ? "active" : ""} onClick={() => setMode("register")}>注册</button>
          </div>
          <form onSubmit={submit}>
            {mode === "register" && (
              <label>
                <span>显示名称</span>
                <input name="display_name" placeholder="怎么称呼你？" maxLength={120} />
              </label>
            )}
            <label>
              <span>用户名</span>
              <input name="username" autoComplete="username" required minLength={3} placeholder="reader" />
            </label>
            <label>
              <span>密码</span>
              <div className="password-field">
                <input
                  name="password"
                  type={showPassword ? "text" : "password"}
                  autoComplete={mode === "login" ? "current-password" : "new-password"}
                  required
                  minLength={8}
                  placeholder="至少 8 个字符"
                />
                <button type="button" onClick={() => setShowPassword((value) => !value)}>
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </label>
            {mode === "register" && (
              <label>
                <span>邀请码（如需要）</span>
                <input name="registration_code" autoComplete="off" />
              </label>
            )}
            {error && <div className="form-error">{error}</div>}
            <button className="primary-button primary-button--wide" disabled={busy}>
              {busy ? "请稍候…" : mode === "login" ? "进入 Flussik" : "创建账号"}
              {!busy && <ArrowRight size={18} />}
            </button>
          </form>
          <small className="auth-fineprint">实验版本 · 词汇估算仅作为阅读个性化的初始范围</small>
        </div>
      </section>
    </div>
  );
}
